// Blog content is fetched from Sanity's public read-only API.
//
// Posts are queried over plain HTTPS with GROQ, so no SDK is needed. The
// dataset must be public (Sanity: Project -> API -> Datasets -> public) —
// nothing here is authenticated, and no token should ever ship in the bundle.
//
// Set REACT_APP_SANITY_PROJECT_ID in frontend/.env. Until it is set, the blog
// renders its empty state rather than erroring.

const PROJECT_ID = process.env.REACT_APP_SANITY_PROJECT_ID || "";
const DATASET = process.env.REACT_APP_SANITY_DATASET || "production";
const API_VERSION = "2024-01-01";

export const isBlogConfigured = () => Boolean(PROJECT_ID);

const endpoint = (query) =>
  `https://${PROJECT_ID}.apicdn.sanity.io/v${API_VERSION}/data/query/${DATASET}?query=${encodeURIComponent(query)}`;

// Only published posts, newest first. `defined(slug.current)` guards against
// drafts saved without a slug, which would otherwise render an unreachable card.
const LIST_QUERY = `*[_type == "post" && !(_id in path("drafts.**")) && defined(slug.current)] | order(publishedAt desc){
  _id,
  title,
  "slug": slug.current,
  excerpt,
  publishedAt,
  category,
  "readingTime": round(length(pt::text(body)) / 5 / 180)
}`;

const postQuery = (slug) => `*[_type == "post" && slug.current == "${slug}" && !(_id in path("drafts.**"))][0]{
  _id,
  title,
  "slug": slug.current,
  excerpt,
  publishedAt,
  category,
  body,
  "readingTime": round(length(pt::text(body)) / 5 / 180)
}`;

async function query(groq) {
  if (!PROJECT_ID) return null;
  const res = await fetch(endpoint(groq));
  if (!res.ok) throw new Error(`Sanity request failed (${res.status})`);
  const json = await res.json();
  return json.result;
}

export async function fetchPosts() {
  const result = await query(LIST_QUERY);
  return result || [];
}

export async function fetchPost(slug) {
  const result = await query(postQuery(slug));
  return result || null;
}

// Sanity stores rich text as Portable Text: an array of blocks, each with
// children spans. This renders the subset used for written posts (headings,
// paragraphs, lists, quotes, bold/italic/links) without pulling in a renderer
// dependency. Unknown block types are skipped rather than crashing the page.
export function portableTextToBlocks(body) {
  if (!Array.isArray(body)) return [];
  const out = [];
  let list = null;

  const flushList = () => {
    if (list) {
      out.push(list);
      list = null;
    }
  };

  body.forEach((block, i) => {
    if (block._type !== "block") {
      flushList();
      return;
    }
    const spans = (block.children || [])
      .filter((c) => c._type === "span")
      .map((c) => ({
        text: c.text || "",
        marks: c.marks || [],
      }));
    if (!spans.some((s) => s.text.trim())) {
      flushList();
      return;
    }

    const item = { key: block._key || `b${i}`, spans, markDefs: block.markDefs || [] };

    if (block.listItem) {
      const type = block.listItem === "number" ? "ol" : "ul";
      if (!list || list.type !== type) {
        flushList();
        list = { kind: "list", type, key: item.key, items: [] };
      }
      list.items.push(item);
      return;
    }

    flushList();
    out.push({ kind: "block", style: block.style || "normal", ...item });
  });

  flushList();
  return out;
}

export function formatPostDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
